import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, Chip, IconButton, MenuItem, Select, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppHeader from "../../../components/header";
import Label from "../../../components/label";
import {
  reseReportSearchFilter,
  setReportEndDate,
  setReportSelectedAgents,
  setReportSelectedStatus,
  setReportStartDate,
} from "../../../lib/redux/slices/searchKeysSlice";
import { getAllAgents, getAllAgentsReport } from "../../../services/AgentApi";
import CustomRangePicker from "../../../components/dateRange";
import { handleOpenSnackBar } from "../../../utils/helper";
import DynamicTable from "../../../components/dynamicTable";
import CustomDropDown from "../../../components/select";
import moment from "moment";
import AppLoader from "../../../components/loader";
import PaginatedTable from "../../../components/paginatedTable";

const tableHeader = [
  { id: 1, title: "Agent", align: "start" },
  { id: 2, title: "Signed", align: "center" },
  { id: 2, title: "Savers", align: "center" },
  { id: 2, title: "Upgrades", align: "center" },
  { id: 6, title: "GP Sold", align: "center" },
  { id: 6, title: "Sales to Target", align: "center" },
  { id: 7, title: "GP % To Target", align: "center" },
  { id: 3, title: "Total Live Sales", align: "center" },
  { id: 4, title: "Rework", align: "center" },
  { id: 5, title: "Sales Lost", align: "center" },
  { id: 7, title: "GP In Provisioning", align: "center" },
  { id: 7, title: "GP Lost", align: "center" },
  { id: 7, title: "GP Live", align: "center" },
  { id: 7, title: "Live % To Target", align: "center" },
  { id: 7, title: "Max Live %", align: "center" },
  { id: 7, title: "Delayed Live", align: "center" },
  { id: 7, title: "Future Live", align: "center" },
];

const displayRows = [
  "agentName",
  "rp_total_signed_contract",
  "report_total_signed_contract_saver",
  "report_total_signed_contract_upgrade",
  "report_gpSold",
  "report_sells_to_target",
  "report_mtd",
  "rp_total_live_sales",
  "rp_gp_in_rework",
  "rp_gp_sales_lost",
  "report_gpInProvisioning",
  "report_gpLoss",
  "report_totalNetProfit",
  "report_mtdLive",
  "report_max_live",
  "report_delayed_live",
  "report_future_live",
];

const dropDownStyle = {
  display: "flex",
  overflowY: "auto",
  gap: 0.5,

  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
    display: "none",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "primary.main",
    // borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "secondary.dark",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "text.coolGrey",
  },
};

let STATUS = [
  {
    id: 1,
    title: "Signed",
    type: "signedContract",
  },
  {
    id: 2,
    title: "GP Sold",
    type: "gpSold",
  },

  {
    id: 3,
    title: "GP In Provisioning",
    type: "gpProvisioning",
  },

  {
    id: 4,
    title: "GP Lost",
    type: "gpLost",
  },

  {
    id: 5,
    title: "Total Live Sales",
    type: "liveSales",
  },

  {
    id: 6,
    title: "GP Live",
    type: "gpLive",
  },
  {
    id: 7,
    title: "Rework",
    type: "gpInRework",
  },

  {
    id: 8,
    title: "Sales Lost",
    type: "salesLost",
  },
  {
    id: 9,
    title: "Delayed Live",
    type: "delayedLive",
  },
  {
    id: 10,
    title: "Future Live",
    type: "futureLive",
  },
];

const ReportingPage = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [allAgents, setAllAgents] = useState([]);
  const [agentReports, setAgentReports] = useState([]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);

  useEffect(() => {
    handleFetchAllGents();
    fetchAgentsReport(
      searchKeys?.reportStartDate,
      searchKeys?.reportEndDate,
      searchKeys?.reportSelectedAgents,
      searchKeys?.selectedReportStatus,
    );
  }, [searchKeys?.selectedReportStatus, searchKeys?.reportSelectedAgents]);

  const fetchAgentsReport = async (startData, endDate, agents, status) => {
    const query = {
      startDate: startData,
      endDate: endDate,
      ids: agents,
      searchBy: status,
    };

    try {
      setIsLoading(true);
      const response = await getAllAgentsReport(query);
      if (response?.data.status === "success") {
        let data = response.data.data;
        setAgentReports(data);
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchAllGents = async () => {
    try {
      const response = await getAllAgents();
      if (response?.data.status === "success") {
        let data = response.data.data;
        setAllAgents(data);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const handleFilterByDate = () => {
    fetchAgentsReport(
      searchKeys?.reportStartDate,
      searchKeys?.reportEndDate,
      searchKeys?.reportSelectedAgents,
      searchKeys?.selectedReportStatus,
    );
  };

  const handleResetFilter = () => {
    dispatch(reseReportSearchFilter());
    fetchAgentsReport(moment(new Date()), moment(new Date()), [], "");
  };

  return (
    <AppHeader>
      <AppLoader isVisible={isLoading} />
      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        alignItems={"flex-end"}
        justifyContent={"flex-end"}
        gap={{ xs: 1, sm: 2 }}
        mb={2}
      >
        <Box width={{ xs: "100%", md: 250 }}>
          <Label title={"select agents"} style={{ fontSize: "0.75rem" }} />
          <Select
            fullWidth
            size="small"
            multiple
            value={searchKeys?.reportSelectedAgents}
            onChange={(e) => {
              dispatch(setReportSelectedAgents(e.target.value));
            }}
            renderValue={(selected) => (
              <Box sx={{ ...dropDownStyle }}>
                {selected.map((id, index) => {
                  const agent = allAgents.find((agent) => agent._id === id);
                  return <Chip key={id} label={agent?.username} />;
                })}
              </Box>
            )}
          >
            {allAgents.map((val, index) => (
              <MenuItem key={index} value={val._id}>
                {val.username}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <Label title={"select Status"} style={{ fontSize: "0.75rem" }} />

          <CustomDropDown
            value={searchKeys?.selectedReportStatus}
            onChange={(e) => dispatch(setReportSelectedStatus(e.target.value))}
            size={"small"}
            stylePropos={{
              width: "250px",
              "& .MuiInputBase-root": {
                backgroundColor: "white",
              },
            }}
          >
            {/* <MenuItem value={searchBy}>All</MenuItem> */}
            {STATUS.map((val) => {
              return (
                <MenuItem key={val.id} value={val.type}>
                  {val.title}
                </MenuItem>
              );
            })}
          </CustomDropDown>
        </Box>
        <CustomRangePicker
          startDate={searchKeys?.reportStartDate}
          endDate={searchKeys?.reportEndDate}
          setStartDate={(start) => dispatch(setReportStartDate(start))}
          setEndDate={(start) => dispatch(setReportEndDate(start))}
          handleFilterDate={handleFilterByDate}
          stackProps={{
            boxShadow: "rgba(0, 0, 0, 0.09) 0px 3px 12px",
            borderRadius: "6px",
          }}
        />
        <IconButton
          onClick={handleResetFilter}
          sx={{
            borderRadius: "10px",
            background: (theme) => theme.palette.gradient.button,
            marginBottom: "8px",
          }}
        >
          <RestartAltIcon htmlColor="white" />
        </IconButton>
      </Stack>

      {/* <DynamicTable
        tableData={agentReports || []}
        displayRows={displayRows}
        tableHeader={tableHeader}
      /> */}
      <PaginatedTable
        // minWidth={1500}
        tableData={agentReports || []}
        displayRows={displayRows}
        tableHeader={tableHeader}
      />
    </AppHeader>
  );
};

export default ReportingPage;
